import { LanguageCode } from '../core';
import { DesignAppProperty } from './design-app-property';
import {
  DesignBranchBranchingEvent,
  DesignMainBranchingEvent
} from './design-branching-event';
import { DesignCommonModule } from './design-common-module';
import { DesignCompoundIntent } from './design-compound-intent';
import { DesignCsvData } from './design-csv-data';
import { DesignDeletedMap } from './design-deleted-map';
import { DesignDialog } from './design-dialog';
import { DesignFillerPrompt } from './design-filler-prompt';
import { DesignFolders } from './design-folders';
import { DesignFunction } from './design-function';
import { DesignGlobalIntent } from './design-global-intent';
import { DesignGraphQlSchema } from './design-graph-ql-schema';
import { DesignIntent } from './design-intent';
import { DesignIntentHandling } from './design-intent-handling';
import { DesignLanguage } from './design-language';
import { DesignMetaData } from './design-meta-data';
import { DesignModule } from './design-module';
import { DesignObject } from './design-object';
import { DesignRootSettings } from './design-root-settings';
import { DesignUnitTest } from './design-unit-test';
import { DesignVariable } from './design-variable';
import { Voice } from './voice';

/** Type for an application's design. */
export type Design = {
  /** @deprecated Use @see Design.branchId. */
  _id: string;
  appCommonModules: DesignCommonModule[];
  appProperties?: DesignAppProperty[];
  /**
   * @deprecated Use @see Design.designName or @see Design.branchName for user
   * facing labels and use @see Design.designId or @see Design.branchId for code.
   */
  application: string;
  /** Dependency version of the Java project version for Process Engine. */
  applicationVersion: string;
  archetypeVersion?: string;
  /** The last time @see applicationVersion was updated. */
  applicationVersionLastChange?: number;
  author: string;
  /**
   * ID for the branch this object is associated with.
   *
   * @note Multiple branches (matched by @see Design.designId) exist within a single design.
   * @note Branches have multiple versions differentiated by @see Design.version.
   */
  branchId: string;
  /**
   * Label of the branch for users to see.
   *
   * @note Everything other than display labels should use @see Design.branchId.
   */
  branchName: string;
  branching?: DesignBranchBranchingEvent[];
  branchingMap?: Record<string, DesignMainBranchingEvent[]>;
  /**
   * @see Design level feature flag for enabling branching & merging features.
   *
   * @note for new @see Design features use @see DesignRootSettings.features.
   */
  branchingMerging?: {
    initialized: boolean;
    lastLocalChange: number;
  };
  ccxml?: string;
  ccxmlLastLocalChange?: number;
  /** @deprecated Use @see Design.clientId or @see Design.clientName. */
  client: string;
  /**
   * ID for the client this object is associated with.
   *
   * @note Clients have multiple designs differentiated by @see Design.designId.
   */
  clientId: string;
  /**
   * Label of the client for users to see.
   *
   * @note Everything other than display labels should use @see Design.clientId.
   */
  clientName: string;
  components: {
    intents: DesignIntent[];
    modules: DesignModule[];
    objects: DesignObject[];
    variables: DesignVariable[];
  };
  compoundIntents: DesignCompoundIntent[];
  csvData: DesignCsvData[];
  /** @deprecated Removed from D2 on 2025-01-03 due to being set, but never read. */
  csvDataLocale?: string;
  currentActiveTab?: string;
  deletedMap?: DesignDeletedMap;
  description?: string;
  descriptionLastLocalChange?: number;
  /**
   * ID for the design this object is associated with.
   *
   * @note Multiple designs (matched by @see Design.clientId) exist within a single client.
   * @note Designs have multiple branches differentiated by @see Design.branchId.
   */
  designId: string;
  /**
   * Label of the design for users to see.
   *
   * @note Everything other than display labels should use @see Design.designId.
   */
  designName: string;
  designer: string;
  dialogs: DesignDialog[];
  displayPrefs: {
    hideDisabledElements: boolean;
  };
  dtmf_live_agent_override_pattern: string;
  dtmf_live_agent_override_patternLastLocalChange?: number;
  fillerPrompts: {
    data_access: DesignFillerPrompt[];
    typing: DesignFillerPrompt[];
    wait: DesignFillerPrompt[];
  };
  folders?: DesignFolders;
  functions: DesignFunction[];
  globalErrorHandling: {
    /** ID of the @see DesignModule to run when errors for an intent. */
    errorHandling: string;
    lastLocalChange?: number;
    lastSavedChange?: number;
    /** ID of the @see DesignModule to run when the user fails to provide an intent to an input in time. */
    returningFromWaitModule?: string;
    /**
     * ID of the @see DesignModule to run when the user starts a call.
     *
     * @note See @see userHangUp for when the user hangs up.
     */
    sessionStart: string;
    /**
     * ID of the @see DesignModule to run when the user hangs up.
     *
     * @note See @see sessionStart for when the user starts a call.
     */
    userHangUp: string;
    /**
     * ID of the @see DesignObject with _class === NodeType.custom to execute after each input.
     */
    postProcessingCustom?: string;
  };
  globalExceptionHandling: {
    /** ID of the @see DesignModule to run when exceptions happen in PE. */
    exception: string;
    lastLocalChange?: number;
    lastSavedChange?: number;
    /** ID of the @see DesignModule to run when timeouts happen in PE. */
    timeout: string;
  };
  globalIntents: DesignGlobalIntent[];
  globalLoopHandling: {
    /**
     * ID of the @see DesignModule to run when the user says the same intent consecutively.
     *
     * @note See @see xAndOut for the non-consecutive version of this.
     */
    inARow: string | null;
    lastLocalChange: number;
    /**
     * ID of the @see DesignModule to run when the user says the same intent multiple times non-consecutively.
     *
     * @note See @see inARow for the consecutive version of this.
     */
    xAndOut: string | null;
  };
  graphQLSchemas: DesignGraphQlSchema[];
  intentHandling: DesignIntentHandling;
  /** @deprecated Moved to @see GlobalSettings.language instead. */
  language?: LanguageCode;
  languages: DesignLanguage[];
  languagesMeta?: {
    lastLocalChange: number;
    lastSavedChange: number;
  };
  lastSavedToRepo: number;
  lastUpdate: number;
  metaData?: DesignMetaData;
  /** @deprecated Moved to @see DesignRootSettings.noInputTimeoutOverride to add channel support. */
  noInputTimeout?: string;
  /** @deprecated Use @see DesignRootSettings.lastLocalChange instead. */
  noInputTimeoutLastLocalChange?: number;
  /**
   * @see Design level feature flag for enabling omni-channel features.
   *
   * @note for new @see Design features use @see DesignRootSettings.features.
   */
  omniChannel: {
    isOmniChannel: boolean;
    lastLocalChange: number;
  };
  previousActiveTab?: string;
  /**
   * @deprecated Use @see Design.designName or @see Design.branchName for user
   * facing labels and use @see Design.designId or @see Design.branchId for code.
   */
  repoApplication: string;
  /** @deprecated Use @see Design.clientId or @see Design.clientName. */
  repoClient: string;
  /** @deprecated Use @see Design.version. PE still uses it so D2 needs to keep it synchronized for now. */
  repoVersion: string;
  rootModule: string;
  rootSettings: DesignRootSettings;
  sensitiveOverrides?: {
    enabled?: boolean;
    lastLocalChange?: number;
  };
  unitTests?: DesignUnitTest[];
  /**
   * Version of this object currently being edited.
   *
   * @note Multiple versions (matched by @see Design.branchId) exist within a single branch.
   */
  version: string;
  /** @deprecated Moved to @see GlobalSettings.voice instead. */
  voice?: Voice;
};

export namespace Design {
  const stringKeys: (keyof(Design))[] = [
    '_id',
    'application',
    'client',
    'designer',
    'dtmf_live_agent_override_pattern',
    'repoApplication',
    'repoClient',
    'repoVersion',
    'rootModule',
    'version'
  ];
  const numberKeys: (keyof(Design))[] = [
    'lastUpdate'
  ];

  export function is(value: unknown): value is Design {
    return !validate(value).length;
  }

  export function validate(
    value: unknown,
    baseKey = ''
  ): [string, { value: unknown; }][] {
    if (value == null || typeof value !== 'object') {
      return [[baseKey, { value }]];
    }

    const validation = [
      ...stringKeys.filter((key) => {
        return typeof (value as Design)[key] !== 'string';
      }),
      ...numberKeys.filter((key) => {
        return typeof (value as Design)[key] !== 'number';
      })
    ].map((key): [string, { value: unknown; }] => {
      return [
        (baseKey) ? `${baseKey}.${key}` : key,
        { value: (value as Design)[key] ?? null }
      ];
    });

    const moduleKey = `${baseKey}.components.modules`;
    const modules = (
      'components' in value
      && value.components
      && typeof value.components === 'object'
      && 'modules' in value.components
      && value.components.modules
    );

    if (modules instanceof Array) {
      validation.concat(
        ...modules.map((appModule, index) => {
          return DesignModule.validate(
            appModule,
            `${moduleKey}[${index}]`
          );
        })
      );
    } else {
      validation.push([moduleKey, { value: modules }]);
    }

    return validation;
  }
}
